import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Config } from 'domain/configs/models/config';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { useEffect, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { MdInfoOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import { listTaxes } from 'services/additionalTax';
import { editConfig } from 'services/config';
import { Mixpanel } from 'services/mixpanel';
import { usePermissions } from 'shared/hooks/usePermissions';
import { useAppTabContext } from '../../../AppContext';
import { getFeesFromDriver } from '../../IntegratedPayment/helpers/feesMapping';

interface EditModalProps {
  isOpen: boolean;
  onClose(): void;
  config: Config;
}

const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose: onCloseFromProps,
  config,
}) => {
  const { app, updateConfig, getDriver } = useAppTabContext();
  const { customer } = useCustomerContext();
  const { d } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(config.trueValue);

  const getRequiredPermission = () => {
    if (config.name === 'feature_toggle_yooga_additional_tax') {
      return 'installation-card-configs-additional_tax-update';
    }
    return 'installation-card-configs-update';
  };

  const getClubeYoogaPayload = async (value: boolean | number) => {
    const fees = getDriver ? getFeesFromDriver(getDriver) : [];
    const payload: {
      customerId?: number;
      marketplaceFeesOld: typeof fees;
      yoogaAdditionalTaxId?: number;
    } = {
      customerId: customer?.id,
      marketplaceFeesOld: fees,
    };

    if (value === true || value === 1) {
      try {
        const taxesResponse = await listTaxes(String(app.idi));
        const activeTax = taxesResponse.data.find(
          (tax: { enabled?: boolean }) => tax.enabled === true,
        );
        if (activeTax) {
          payload.yoogaAdditionalTaxId = activeTax.id;
        }
      } catch (e) {
        console.error('Não foi possível obter os dados da taxa adicional', e);
      }
    }

    return payload;
  };

  const requiredPermission = getRequiredPermission();

  const onClose = () => {
    onCloseFromProps();
    setLoading(false);
    setToken(config.trueValue);
  };

  const buildPayload = async () => {
    let processedValue: string | number = token as string | number;
    if (config.type === 'SELECTION' && Object.keys(config.options).length) {
      const selectedOption = Object.values(config.options).find(
        (option) => String(option.value) === String(token),
      );
      if (selectedOption) {
        processedValue = selectedOption.value;
      }
    }

    const payload = {
      name: config.name,
      value: processedValue,
    };

    if (payload.name === 'feature_toggle_yooga_additional_tax') {
      const clubeYoogaPayload = await getClubeYoogaPayload(
        payload.value as boolean | number,
      );
      return {
        ...payload,
        ...clubeYoogaPayload,
      };
    }

    return payload;
  };

  const submit = async () => {
    if (d(requiredPermission)) {
      toast.error('Você não possui permissão para editar esta configuração');
      return;
    }

    setLoading(true);

    Mixpanel.track('customers-app-configs-submitted-edit-value');

    try {
      const payload = await buildPayload();
      const response = await editConfig(String(app.idi), payload);
      toast.success('Configuração atualizada');
      updateConfig(response.data.configs);
      onCloseFromProps();
    } catch {
      toast.error('Falha ao atualizar configuração');
    } finally {
      setLoading(false);
    }
  };

  const isVmIntegration = config.name === 'vm_integration_code';
  const [vmEnabled, setVmEnabled] = useState(
    isVmIntegration && config.trueValue !== '0' && !!config.trueValue,
  );

  useEffect(() => {
    if (isVmIntegration) {
      const enabled = config.trueValue !== '0' && !!config.trueValue;
      setVmEnabled(enabled);
      setToken(enabled ? String(app.idi) : '0');
    } else {
      setToken(config.trueValue);
    }
  }, [config, isVmIntegration, app.idi]);

  const handleVmToggle = () => {
    const newEnabled = !vmEnabled;
    setVmEnabled(newEnabled);
    setToken(newEnabled ? String(app.idi) : '0');
  };

  let inputType = (
    <Textarea
      placeholder="Inserir valor da configuração"
      resize="none"
      isDisabled={loading || isVmIntegration}
      isReadOnly={isVmIntegration}
      value={String(token)}
      onChange={(e) => setToken(e.target.value)}
    />
  );

  if (config.type == 'SELECTION' && Object.keys(config.options).length) {
    inputType = (
      <Menu>
        <MenuButton
          as={Button}
          variant="outline"
          textAlign={'left'}
          fontWeight="400"
          colorScheme="'gray'"
          rightIcon={<FaChevronDown />}
          w="100%"
        >
          {config.getOptionLabel(Number(token))}
        </MenuButton>
        <MenuList w={'200px'}>
          {Object.values(config.options).map((option) => (
            <MenuItem onClick={() => setToken(String(option.value))} bg="white">
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    );
  }

  return (
    <Modal
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay />

      <ModalContent>
        <ModalCloseButton isDisabled={loading} />
        <ModalHeader>
          <HStack>
            <Text>{config.name}</Text>
          </HStack>
        </ModalHeader>

        <ModalBody as={Stack} gap="1rem">
          {config.description && (
            <Card bg="blue.100">
              <CardBody>
                <HStack gap="1rem">
                  <Icon as={MdInfoOutline} color="blue.500" />
                  <Text fontSize="0.9rem" fontWeight="400" color="gray.600">
                    {config.description}
                  </Text>
                </HStack>
              </CardBody>
            </Card>
          )}

          {isVmIntegration && (
            <FormControl>
              <HStack>
                <Switch
                  size="sm"
                  isChecked={vmEnabled}
                  onChange={handleVmToggle}
                  isDisabled={loading}
                />
                <Text fontSize="0.9rem">
                  {vmEnabled ? 'Ativado' : 'Desativado'}
                </Text>
              </HStack>
            </FormControl>
          )}

          <FormControl>
            <FormLabel>Valor manual</FormLabel>
            {inputType}
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            onClick={submit}
            colorScheme="yooga"
            w="100%"
            isDisabled={d(requiredPermission)}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditModal;
